/**
 * B1 · Business Dashboard — mounted at `/business` (list) and
 * `/business/:id` (single). Server-side RBAC is authoritative; this
 * screen only decides what to render, never what's permitted.
 */
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Building2, Users, Package, Star, TrendingUp, Plus, ChevronLeft, ChevronRight, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function BusinessHub() {
  const { t } = useI18n();
  const [busy, setBusy] = useState(true);
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const nav = useNavigate();

  const load = () => api.get("/business/mine")
    .then(r => setItems(r.data.businesses || []))
    .catch(() => toast.error(t("business.load_error", "Could not load businesses")))
    .finally(() => setBusy(false));
  useEffect(() => { load(); }, []);

  return (
    <div className="min-h-screen bg-stone-50 pb-24" data-testid="business-hub">
      <div className="sticky top-0 z-20 bg-white border-b border-stone-200">
        <div className="max-w-3xl mx-auto flex items-center gap-3 px-4 py-3">
          <button onClick={() => nav(-1)} aria-label={t("common.back","Back")}><ChevronLeft size={20}/></button>
          <div className="flex-1">
            <div className="text-[11px] uppercase tracking-widest text-stone-500 font-black">{t("me.section","Account")}</div>
            <div className="font-black text-stone-900 text-base flex items-center gap-2"><Building2 size={18}/> {t("business.title", "My businesses")}</div>
          </div>
          <button onClick={() => setCreating(true)} data-testid="business-create-btn"
                  className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-black px-3 py-1.5 inline-flex items-center gap-1">
            <Plus size={13}/> {t("business.new", "New")}
          </button>
        </div>
      </div>
      <div className="max-w-3xl mx-auto p-4 space-y-3">
        {busy && <div className="text-center py-10 text-stone-400">Loading…</div>}
        {!busy && !items.length && (
          <div className="text-center py-16 text-stone-400" data-testid="business-empty">
            <Building2 size={32} className="inline"/>
            <div className="mt-2 text-sm font-black">{t("business.empty", "You don't own a business yet")}</div>
            <div className="text-xs">{t("business.empty.sub", "Create one to unlock the Business Dashboard.")}</div>
          </div>
        )}
        {items.map(b => (
          <Link key={b.business_id} to={`/business/${b.business_id}`}
                data-testid={`business-row-${b.business_id}`}
                className="rounded-2xl bg-white border border-stone-200 p-4 flex items-center gap-3 hover:border-emerald-300">
            <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-700 grid place-items-center">
              <Building2 size={20}/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-black text-stone-900 text-sm truncate">{b.name}</div>
              <div className="text-[11px] text-stone-500 truncate">{b.category} · {b.city || "—"} · <span className="uppercase font-black">{b.role}</span></div>
              <div className="mt-1 h-1 bg-stone-100 rounded-full overflow-hidden">
                <div className="h-full bg-emerald-500" style={{ width: `${b.profile_completion || 0}%` }}/>
              </div>
            </div>
            <ChevronRight size={16} className="text-stone-400"/>
          </Link>
        ))}
      </div>
      {creating && <CreateSheet onClose={() => setCreating(false)} onDone={(b) => { setCreating(false); nav(`/business/${b.business_id}`); }}/>}
    </div>
  );
}

function CreateSheet({ onClose, onDone }) {
  const { t } = useI18n();
  const [form, setForm] = useState({ name: "", category: "", city: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.name.trim() || !form.category.trim()) {
      toast.error(t("business.required","Name and category are required")); return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/business", form);
      toast.success(t("business.created","Business created"));
      onDone(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error","Something went wrong"));
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center p-2" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-md p-5 space-y-3" onClick={e => e.stopPropagation()} data-testid="business-create-sheet">
        <div className="font-black text-stone-900">{t("business.new.title","Create business")}</div>
        <input value={form.name} onChange={e => setForm({...form, name: e.target.value})}
               placeholder={t("business.name","Business name")}
               data-testid="business-input-name"
               className="w-full rounded-xl border border-stone-200 px-3 py-2 text-sm"/>
        <input value={form.category} onChange={e => setForm({...form, category: e.target.value})}
               placeholder={t("business.category","Category (e.g. retail, food)")}
               data-testid="business-input-category"
               className="w-full rounded-xl border border-stone-200 px-3 py-2 text-sm"/>
        <input value={form.city} onChange={e => setForm({...form, city: e.target.value})}
               placeholder={t("business.city","City")}
               data-testid="business-input-city"
               className="w-full rounded-xl border border-stone-200 px-3 py-2 text-sm"/>
        <div className="flex gap-2 pt-1">
          <button onClick={onClose} className="flex-1 rounded-full bg-stone-100 text-stone-700 font-black text-xs py-2">{t("common.cancel","Cancel")}</button>
          <button onClick={submit} disabled={busy}
                  data-testid="business-create-submit"
                  className="flex-1 rounded-full bg-emerald-600 text-white font-black text-xs py-2 disabled:opacity-50">{busy?t("common.working","Working…"):t("common.create","Create")}</button>
        </div>
      </div>
    </div>
  );
}

export function BusinessDashboard() {
  const { t } = useI18n();
  const { id } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [editingGst, setEditingGst] = useState(false);
  const load = () => api.get(`/business/${id}/dashboard`)
      .then(r => setD(r.data))
      .catch(() => toast.error(t("business.dash.error","Could not load dashboard")));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  if (!d) return <div className="p-10 text-center text-stone-400">Loading…</div>;
  const c = d.counts || {};
  const tiles = [
    { lbl: t("business.leads","Leads"),    val: c.leads,    Icon: TrendingUp, color: "text-sky-600 bg-sky-50 border-sky-200" },
    { lbl: t("business.orders","Orders"),  val: c.orders,   Icon: Package,    color: "text-fuchsia-600 bg-fuchsia-50 border-fuchsia-200" },
    { lbl: t("business.products","Products"), val: c.products, Icon: Package, color: "text-amber-600 bg-amber-50 border-amber-200" },
    { lbl: t("business.reviews","Reviews"), val: c.reviews, Icon: Star,      color: "text-yellow-600 bg-yellow-50 border-yellow-200" },
    { lbl: t("business.team","Team"),      val: c.team,     Icon: Users,     color: "text-emerald-600 bg-emerald-50 border-emerald-200" },
  ];
  return (
    <div className="min-h-screen bg-stone-50 pb-24" data-testid="business-dashboard">
      <div className="sticky top-0 z-20 bg-white border-b border-stone-200">
        <div className="max-w-3xl mx-auto flex items-center gap-3 px-4 py-3">
          <button onClick={() => nav(-1)} aria-label={t("common.back","Back")}><ChevronLeft size={20}/></button>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest text-stone-500 font-black flex items-center gap-1">
              <ShieldCheck size={12}/> {d.role}
            </div>
            <div className="font-black text-stone-900 text-base">{d.business?.name}</div>
          </div>
        </div>
      </div>
      <div className="max-w-3xl mx-auto p-4 space-y-4">
        <div className="rounded-2xl bg-white border border-stone-200 p-4">
          <div className="text-xs font-black text-stone-700 mb-1">{t("business.completion","Profile completion")}</div>
          <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
            <div className="h-full bg-emerald-500" style={{ width: `${d.profile_completion}%` }} data-testid="business-completion-bar"/>
          </div>
          <div className="text-[10px] text-stone-500 mt-1">{d.profile_completion}%</div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {tiles.map(({ lbl, val, Icon, color }) => (
            <div key={lbl} className={`rounded-xl border p-3 ${color}`}>
              <Icon size={16}/>
              <div className="text-lg font-black mt-1">{val ?? 0}</div>
              <div className="text-[10px] font-black uppercase tracking-wide">{lbl}</div>
            </div>
          ))}
        </div>
        {/* GST / Legal identity */}
        <BusinessGstSection biz={d.business} onSaved={load} onEdit={() => setEditingGst(true)}/>
        {editingGst && <GstEditSheet biz={d.business} onClose={() => setEditingGst(false)}
                                       onSaved={() => { setEditingGst(false); load(); }}/>}
        <div className="rounded-2xl bg-white border border-stone-200 p-4">
          <div className="text-xs uppercase tracking-widest text-stone-500 font-black mb-2">{t("business.quick","Quick actions")}</div>
          <div className="grid grid-cols-2 gap-2">
            {(d.quick_actions || []).map(a => (
              <button key={a.key} data-testid={`business-action-${a.key}`}
                      onClick={() => a.key === "add_product" ? nav(`/business/${id}/catalogue`) :
                                     a.key === "view_orders" ? nav(`/business/${id}/orders`) :
                                     a.key === "manage_team" ? nav(`/business/${id}`) : null}
                      className="rounded-xl border border-stone-200 bg-stone-50 hover:bg-stone-100 text-xs font-black text-stone-700 px-3 py-2 text-left">
                {a.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}


function BusinessGstSection({ biz, onEdit }) {
  const { t } = useI18n();
  const g = biz?.gstin;
  return (
    <div className="rounded-2xl bg-white border border-stone-200 p-4" data-testid="business-gst-section">
      <div className="flex items-center justify-between gap-2">
        <div>
          <div className="text-xs uppercase tracking-widest text-stone-500 font-black">{t("business.gst.title","GST & Legal details")}</div>
          <div className="text-sm font-black text-stone-900 mt-1" data-testid="business-gst-value">
            {g || <span className="text-stone-400">{t("business.gst.none","No GSTIN on file")}</span>}
          </div>
          {biz?.legal_name && <div className="text-[11px] text-stone-500 mt-0.5">{biz.legal_name}</div>}
        </div>
        <button onClick={onEdit} data-testid="business-gst-edit"
                className="rounded-full bg-stone-900 text-white text-xs font-black px-3 py-1.5">
          {g ? t("business.gst.edit","Edit") : t("business.gst.add","Add GSTIN")}
        </button>
      </div>
      <div className="mt-2 text-[11px] text-stone-500 leading-relaxed">
        {t("business.gst.note",
           "Stored on the business. Every invoice snapshots these details at creation time — historical invoices are not affected by later changes.")}
      </div>
    </div>
  );
}


function GstEditSheet({ biz, onClose, onSaved }) {
  const { t } = useI18n();
  const [form, setForm] = useState({
    gstin: biz?.gstin || "",
    legal_name: biz?.legal_name || "",
    pan: biz?.pan || "",
    registered_address: biz?.registered_address || "",
    state_code: biz?.state_code || "",
  });
  const [val, setVal] = useState(null);   // {format, checksum, verified, status, verifier}
  const [busy, setBusy] = useState(false);
  const validate = async () => {
    if (!form.gstin.trim()) { setVal(null); return; }
    try {
      const { data } = await api.post(`/business/${biz.business_id}/gst/gstin/validate`, { gstin: form.gstin });
      setVal(data);
    } catch { setVal(null); }
  };
  useEffect(() => { const t = setTimeout(validate, 350); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [form.gstin]);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/business/${biz.business_id}`, form);
      toast.success(t("business.gst.saved","GST details saved"));
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error","Something went wrong"));
    } finally { setBusy(false); }
  };

  const badge = () => {
    if (!val || !form.gstin) return null;
    const st = val.status;
    if (st === "format_invalid")
      return <span className="text-rose-700 bg-rose-50 rounded-full px-2 py-0.5 text-[10px] font-black" data-testid="gst-status-format-invalid">Format invalid</span>;
    if (st === "checksum_invalid")
      return <span className="text-amber-700 bg-amber-50 rounded-full px-2 py-0.5 text-[10px] font-black" data-testid="gst-status-checksum-invalid">Format valid · checksum failed</span>;
    if (st === "officially_verified")
      return <span className="text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5 text-[10px] font-black" data-testid="gst-status-verified">Officially verified</span>;
    if (st === "verification_failed")
      return <span className="text-amber-700 bg-amber-50 rounded-full px-2 py-0.5 text-[10px] font-black" data-testid="gst-status-verification-failed">Checksum valid · verifier rejected</span>;
    if (st === "verification_unavailable")
      return <span className="text-sky-700 bg-sky-50 rounded-full px-2 py-0.5 text-[10px] font-black" data-testid="gst-status-unavailable">Checksum valid · verification unavailable</span>;
    return null;
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center p-2" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-md p-5 space-y-3" onClick={e => e.stopPropagation()} data-testid="gst-edit-sheet">
        <div className="font-black text-stone-900">{t("business.gst.title","GST & Legal details")}</div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-stone-500 font-black">GSTIN</label>
          <input value={form.gstin} onChange={e => setForm({ ...form, gstin: e.target.value.toUpperCase() })}
                 placeholder="e.g. 27AAPFU0939F1ZV" maxLength={15}
                 data-testid="gst-input-gstin"
                 className="w-full rounded-xl border border-stone-200 px-3 py-2 text-sm font-mono uppercase tracking-wide"/>
          <div className="mt-1.5">{badge()}</div>
          {val?.verifier && !val.verifier.configured && val.status === "verification_unavailable" && (
            <div className="mt-1 text-[10px] text-stone-500">
              No authorized GST verification provider is configured yet. Format and checksum are validated locally.
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-stone-500 font-black">Legal name</label>
            <input value={form.legal_name} onChange={e => setForm({ ...form, legal_name: e.target.value })}
                   data-testid="gst-input-legal-name"
                   className="w-full rounded-xl border border-stone-200 px-3 py-2 text-sm"/>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-stone-500 font-black">PAN</label>
            <input value={form.pan} onChange={e => setForm({ ...form, pan: e.target.value.toUpperCase() })}
                   data-testid="gst-input-pan" maxLength={10}
                   className="w-full rounded-xl border border-stone-200 px-3 py-2 text-sm font-mono uppercase"/>
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-stone-500 font-black">Registered address</label>
          <textarea value={form.registered_address} onChange={e => setForm({ ...form, registered_address: e.target.value })}
                    data-testid="gst-input-address" rows={2}
                    className="w-full rounded-xl border border-stone-200 px-3 py-2 text-sm"/>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-stone-500 font-black">State code</label>
          <input value={form.state_code} onChange={e => setForm({ ...form, state_code: e.target.value })}
                 maxLength={2} placeholder="27"
                 data-testid="gst-input-state-code"
                 className="w-24 rounded-xl border border-stone-200 px-3 py-2 text-sm font-mono"/>
        </div>
        <div className="flex gap-2 pt-1">
          <button onClick={onClose} className="flex-1 rounded-full bg-stone-100 text-stone-700 font-black text-xs py-2">{t("common.cancel","Cancel")}</button>
          <button onClick={save} disabled={busy}
                  data-testid="gst-save-btn"
                  className="flex-1 rounded-full bg-emerald-600 text-white font-black text-xs py-2 disabled:opacity-50">
            {busy ? t("common.working","Working…") : t("common.save","Save")}
          </button>
        </div>
      </div>
    </div>
  );
}
